import React from 'react';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import { useDocument } from '@nandorojo/swr-firestore';
import { IhomepageSetting } from '@typings/setting/homepageSetting.interface';
import { SettingCollectionPath } from '@typings/collections.enum';

const Container = styled(motion.div) <{ bgColor?: string }>`
  width: 100vw;
  height: 100dvh;
  position: fixed;
  top: 0;
  left: 0;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  gap: 20px;
  background-color: ${props => props.bgColor || 'var(--backgroundColor)'};
  z-index: 100;
`;

// 로딩 디자인 컴포넌트들
const SpinnerLoader = styled.div<{ color?: string }>`
  width: 50px;
  height: 50px;
  border: 5px solid rgba(255, 255, 255, 0.2);
  border-top-color: ${props => props.color || 'var(--primaryColor)'};
  border-radius: 50%;
  animation: spin 1s linear infinite;

  @keyframes spin {
    to { transform: rotate(360deg); }
  }
`;

const BarLoader = styled.div<{ color?: string; bgColor?: string }>`
  width: 250px;
  height: 10px;
  background-color: ${props => props.bgColor || 'rgba(255, 255, 255, 0.2)'};
  border-radius: 10px;
  overflow: hidden;
  position: relative;

  &::after {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    height: 100%;
    width: 40%;
    background-color: ${props => props.color || 'var(--primaryColor)'};
    border-radius: 10px;
    animation: slide 1.5s ease-in-out infinite;
  }

  @keyframes slide {
    0%, 100% { left: -40%; }
    50% { left: 100%; }
  }
`;

const DotsLoader = styled.div<{ color?: string }>`
  display: flex;
  gap: 10px;

  span {
    width: 15px;
    height: 15px;
    background-color: ${props => props.color || 'var(--primaryColor)'};
    border-radius: 50%;
    animation: bounce 1.4s infinite ease-in-out both;

    &:nth-child(1) { animation-delay: -0.32s; }
    &:nth-child(2) { animation-delay: -0.16s; }
  }

  @keyframes bounce {
    0%, 80%, 100% { transform: scale(0); }
    40% { transform: scale(1); }
  }
`;

const PulseLoader = styled.div<{ color?: string }>`
  width: 60px;
  height: 60px;
  background-color: ${props => props.color || 'var(--primaryColor)'};
  border-radius: 50%;
  animation: pulse 1.2s cubic-bezier(0, 0, 0.2, 1) infinite;

  @keyframes pulse {
    0%, 100% { opacity: 1; transform: scale(0); }
    50% { opacity: 0.5; transform: scale(1); }
  }
`;

const RingLoader = styled.div<{ color?: string }>`
  width: 60px;
  height: 60px;
  border-radius: 50%;
  border: 6px solid transparent;
  border-top-color: ${props => props.color || 'var(--primaryColor)'};
  border-bottom-color: ${props => props.color || 'var(--primaryColor)'};
  animation: rotate 1s linear infinite;

  @keyframes rotate {
    to { transform: rotate(360deg); }
  }
`;

const WaveLoader = styled.div<{ color?: string }>`
  display: flex;
  gap: 6px;
  align-items: center;

  span {
    width: 8px;
    height: 50px;
    background-color: ${props => props.color || 'var(--primaryColor)'};
    border-radius: 4px;
    animation: wave 1s ease-in-out infinite;

    &:nth-child(1) { animation-delay: 0s; }
    &:nth-child(2) { animation-delay: 0.1s; }
    &:nth-child(3) { animation-delay: 0.2s; }
    &:nth-child(4) { animation-delay: 0.3s; }
    &:nth-child(5) { animation-delay: 0.4s; }
  }

  @keyframes wave {
    0%, 100% { height: 25px; }
    50% { height: 50px; }
  }
`;

const LoadingText = styled.div<{ color?: string }>`
  font-size: 18px;
  font-weight: 500;
  color: ${props => props.color || 'var(--textColor)'};
`;

const container = {
  show: {
    transition: {
      children: 0.35,
    },
  },
};

type Props = {
  setLoading?: any;
};

function LoaderPage({ setLoading }: Props) {
  const { data: homeSet } = useDocument<IhomepageSetting>(
    SettingCollectionPath.HOMEPAGE,
    { listen: false }
  );

  const loadingConfig = homeSet?.loading || {
    design: 'spinner',
    text: '로딩 중...',
    color: '#4ecdc4',
    backgroundColor: 'rgba(0, 0, 0, 0.9)',
  };

  const renderLoader = () => {
    switch (loadingConfig.design) {
      case 'spinner':
        return <SpinnerLoader color={loadingConfig.color} />;
      case 'bar':
        return <BarLoader color={loadingConfig.color} bgColor={loadingConfig.backgroundColor} />;
      case 'dots':
        return (
          <DotsLoader color={loadingConfig.color}>
            <span /><span /><span />
          </DotsLoader>
        );
      case 'pulse':
        return <PulseLoader color={loadingConfig.color} />;
      case 'ring':
        return <RingLoader color={loadingConfig.color} />;
      case 'wave':
        return (
          <WaveLoader color={loadingConfig.color}>
            <span /><span /><span /><span /><span />
          </WaveLoader>
        );
      default:
        return <SpinnerLoader color={loadingConfig.color} />;
    }
  };

  return (
    <Container
      variants={container}
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      bgColor={loadingConfig.backgroundColor}
    >
      {renderLoader()}
      <LoadingText color={loadingConfig.color}>{loadingConfig.text}</LoadingText>
    </Container>
  );
}

export default LoaderPage;
